// =============================================================================
// Rohantwort von Directus in die Typen aus typen.ts umwandeln
// =============================================================================
//
// Warum diese Schicht ueberhaupt existiert: Die Typen in typen.ts sind ein
// Versprechen an den Rest der App ("zielgruppen IST eine Liste"). Ein
// TypeScript-Interface allein prueft aber nichts zur Laufzeit - es beschreibt
// nur, was der Compiler annehmen soll. Kaeme aus Directus etwas anderes,
// waere das Versprechen still gebrochen, und der Fehler fiele erst weit
// entfernt auf.
//
// Diese Datei ist die einzige Stelle, an der "unknown" zu einem echten Typ
// wird. Alles danach darf sich auf die Typen verlassen.
// =============================================================================

import type {
  Dokument,
  DokumentVerweis,
  Glossareintrag,
  Phase,
  PhasenVerweis,
  Termin,
} from './typen';

type Roh = Record<string, unknown>;

/** Damit dieselbe Warnung nicht bei jedem Datensatz erneut erscheint. */
const schonGewarnt = new Set<string>();

function warneEinmal(schluessel: string, text: string): void {
  if (schonGewarnt.has(schluessel)) return;
  schonGewarnt.add(schluessel);
  console.warn(`[Directus] ${text}`);
}

function alsText(wert: unknown): string | null {
  if (typeof wert === 'string' && wert.trim() !== '') return wert;
  return null;
}

function alsZahl(wert: unknown): number {
  const n = typeof wert === 'number' ? wert : Number(wert);
  return Number.isFinite(n) ? n : 0;
}

/** Monate einer Phase als Zahlen 1-12. Directus liefert ein json-Feld. */
function alsMonate(wert: unknown, herkunft: string): number[] {
  if (!Array.isArray(wert)) {
    if (wert !== null && wert !== undefined) {
      warneEinmal('monate-keine-liste', `${herkunft}: monate ist keine Liste.`);
    }
    return [];
  }
  const monate: number[] = [];
  for (const eintrag of wert) {
    const m = alsZahl(eintrag);
    if (m >= 1 && m <= 12 && !monate.includes(m)) monate.push(m);
  }
  return monate;
}

function alsPhasenVerweis(wert: unknown): PhasenVerweis | null {
  if (wert === null || wert === undefined || typeof wert !== 'object') return null;
  const roh = wert as Roh;
  return {
    id: alsZahl(roh['id']),
    nummer: alsZahl(roh['nummer']),
    name: alsText(roh['name']) ?? 'Ohne Namen',
  };
}

function alsDokumentVerweis(wert: unknown): DokumentVerweis | null {
  if (wert === null || wert === undefined || typeof wert !== 'object') return null;
  const roh = wert as Roh;
  return {
    id: alsZahl(roh['id']),
    titel: alsText(roh['titel']) ?? 'Ohne Titel',
    datei: alsText(roh['datei']),
  };
}

export function alsPhase(roh: Roh): Phase {
  return {
    id: alsZahl(roh['id']),
    nummer: alsZahl(roh['nummer']),
    name: alsText(roh['name']) ?? 'Ohne Namen',
    zeitraum: alsText(roh['zeitraum']),
    monate: alsMonate(roh['monate'], `Phase ${String(roh['nummer'])}`),
    schwerpunkt: alsText(roh['schwerpunkt']),
  };
}

export function alsTermin(roh: Roh): Termin {
  const titel = alsText(roh['titel']) ?? 'Ohne Titel';
  return {
    id: alsZahl(roh['id']),
    titel,
    datum: alsText(roh['datum']) ?? '',
    phase: alsPhasenVerweis(roh['phase']),
    dokument: alsDokumentVerweis(roh['dokument']),
    aktionstext: alsText(roh['aktionstext']),
  };
}

export function alsGlossareintrag(roh: Roh): Glossareintrag {
  return {
    id: alsZahl(roh['id']),
    begriff: alsText(roh['begriff']) ?? 'Ohne Begriff',
    erklaerung: alsText(roh['erklaerung']),
    rechtsgrundlage: alsText(roh['rechtsgrundlage']),
    dokument: alsDokumentVerweis(roh['dokument']),
  };
}

export function alsDokument(roh: Roh): Dokument {
  return {
    id: alsZahl(roh['id']),
    titel: alsText(roh['titel']) ?? 'Ohne Titel',
    datei: alsText(roh['datei']),
    kategorie: alsText(roh['kategorie']),
    version: alsText(roh['version']),
    phase: alsPhasenVerweis(roh['phase']),
    schnellzugriff: roh['schnellzugriff'] === true,
  };
}

/** Termine ohne Datum kann die Ampel nicht einordnen. Sie fliegen hier raus,
 *  statt spaeter als "seit 20000 Tagen ueberfaellig" aufzutauchen. */
export function hatBrauchbaresDatum(t: Termin): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t.datum)) {
    warneEinmal(
      `termin-ohne-datum-${String(t.id)}`,
      `Termin "${t.titel}" hat kein brauchbares Datum und wird nicht angezeigt.`,
    );
    return false;
  }
  return true;
}
